
import moment from 'moment';
import { EventEmitterX } from '../../../helpers/events';
import { defaultAgent, apiResponse } from '../../../helpers/web-agent';
import { RECORD_FOUND } from '../../../constants/messages';
import { uuid } from '../../../helpers/common';
import { Shape } from '../geo/DrawingTools';
import { toDisplayTimeString, secondsToTime, dateToUTC } from '../../../helpers/moment-ex';

export const STREAM_FETCHING = "fetching";
export const STREAM_FETCHED = "fetched";
export const EVENT_CLEAR_STREAM = "workwindow_clear_stream";

export default class WorkWindow extends EventEmitterX {
    constructor(map, google) {
        super();

        this.map = map;
        this.google = google;

        this.egIdWiseList = {};
        this.workWindows = {};
        this.shapes = {};
        this.defaultShapeProperties = {
            fillColor: "#ffc107",
            fillOpacity: 1,
            strokeColor: "#ffc107",
            strokeWeight: 2,
            strokeOpacity: 1
        };

        this.reqParams = {
            selectedEgIds: [],
            farmId: null,
            timezone: null,
            startDate: null,
            endDate: null,
            startUTCDate: null,
            endUTCDate: null
        };

        this.onBeforeDraw = null;
    }

    request(params) {
        this.workWindows = {};
        this.reqParams = {
            ...this.reqParams, ...params,
            startDate: moment.utc(params.startDate),
            endDate: moment.utc(params.endDate),
            startUTCDate: dateToUTC(params.startDate, params.timezone),
            endUTCDate: dateToUTC(params.endDate, params.timezone)
        };

        if (params.egList && Array.isArray(params.egList)) {
            this.egIdWiseList = {};
            params.egList.forEach(e => this.egIdWiseList[e.egId] = e);
        }

        return this;
    }

    stream() {
        try {
            this.emit(STREAM_FETCHING);
            return new Promise((resolve) => {
                const done = () => {
                    this.emit(STREAM_FETCHED);
                    return resolve();
                };
                this.fetchWorkWindow(this.reqParams).then(done).catch(done);
            });
        } catch (ex) {
            throw ex;
        }
    }

    fetchWorkWindow(params) {
        this.workWindows = {};
        const reqParams = {
            farm_uuid: params.farmId,
            start_date: params.startUTCDate.format(),
            end_date: params.endUTCDate.format(),
        };

        if (params.selectedEgIds.length === 1) {
            const egId = params.selectedEgIds[0];
            if (!this.egIdWiseList[egId]) this.combineWorkWindow(null);

            const isTruck = this.egIdWiseList[egId].isTruck;
            reqParams[isTruck ? "transporter_equipment_group_uuid" : "equipment_group_uuid"] = egId;
        }

        const requestId = `workwindow_workwindowAPI`;
        return defaultAgent.get("/api/farm/workwindow", reqParams, { requestId, timeout: (3 * 60 * 1000) })
            .then(res => {
                const agentResult = apiResponse(res, []);
                if (agentResult.code === RECORD_FOUND.code) this.combineWorkWindow(agentResult.data);
                else if (agentResult.message) console.log(agentResult.message);
                return (Object.keys(this.workWindows).length > 0);
            });
    }

    combineWorkWindow(work) {
        if (!(work && work.data && Array.isArray(work.data))) return;

        for (const item of work.data) {
            if (!(item["work_windows"] && item["work_windows"].length > 0)) continue;

            for (const window of item["work_windows"]) {
                if (!(window.shape && window.shape.coordinates.length > 0)) continue;

                if (this.reqParams.selectedEgIds.indexOf(window.equipment_group_uuid) >= 0)
                    this._workWindows(window, window.equipment_group_uuid);
                else if (this.reqParams.selectedEgIds.indexOf(window.transporter_equipment_group_uuid) >= 0)
                    this._workWindows(window, window.transporter_equipment_group_uuid);
            }
        }

        return this;
    }

    _workWindows(window, wwEgId) {
        if (!wwEgId || !this.egIdWiseList[wwEgId]) return;
        if (this.reqParams.selectedEgIds.indexOf(wwEgId) < 0) return;
        if (!this.workWindows[wwEgId]) this.workWindows[wwEgId] = {};

        let strokeColor = null;
        const transporterId = window.transporter_equipment_group_uuid;
        if (transporterId && transporterId !== wwEgId && this.egIdWiseList[transporterId])
            strokeColor = this.egIdWiseList[transporterId].color;

        const egItem = this.egIdWiseList[wwEgId];
        const windowId = uuid();

        this.workWindows[wwEgId][windowId] = {
            windowId,
            startTime: window.start_time,
            endTime: window.end_time,
            sourceId: window.equipment_group_uuid,
            transporterId: window.transporter_equipment_group_uuid,
            shape: {
                type: window.shape.type.toLowerCase(),
                location: window.shape.coordinates[0].map(e => ({ lat: e[1], lng: e[0] })),
                properties: {
                    fillColor: egItem.color,
                    zIndex: 99,
                    ...strokeColor ? { strokeColor, strokeWeight: 4 } : {}
                }
            },
            ...window.data ? { data: window.data } : {}
        };
    }

    draw() {
        const shapeObj = this._getShapeInstance();

        for (const egId in this.workWindows) {
            for (const windowId in this.workWindows[egId]) {
                const window = this.workWindows[egId][windowId];
                if (this.onBeforeDraw && !this.onBeforeDraw(window)) continue;
                const shapeDetail = {
                    ...window.shape, properties: {
                        ...this.defaultShapeProperties,
                        ...window.shape.properties
                    }
                };

                const shape = shapeObj.createShape({ ...shapeDetail, id: windowId });
                if (!shape) continue;
                shape.type = shapeDetail.type;
                shape.setMap(this.map);
                this._addShapeEventListener(shape);

                const shapeId = shape.id;
                if (!this.shapes[shapeId]) this.shapes[shapeId] = {};
                this.shapes[shapeId] = {
                    egId, shape, infowindow: null,
                    ...window.data ? { data: window.data } : {}
                };
            }
        }
        return this;
    }

    setBounds() {
        let bounds = null;
        for (const egId in this.workWindows) {
            for (const windowId in this.workWindows[egId]) {
                const shapeDetail = this.workWindows[egId][windowId].shape;
                for (const coordinate of shapeDetail.location) {
                    if (!bounds) bounds = new this.google.maps.LatLngBounds();
                    const point = new this.google.maps.LatLng(coordinate.lat, coordinate.lng);
                    bounds.extend(point);
                }
            }
        }

        if (bounds) this.map.fitBounds(bounds);
        return this;
    }

    _addShapeEventListener(newShape) {
        const google = this.google;
        const me = this;

        if (newShape.type === google.maps.drawing.OverlayType.MARKER) {
            google.maps.event.addListener(newShape, 'click', e => {
                me.openShapeInfowindow(newShape, e.latLng);
            });
        }
        else {
            // Add an event listener that selects the newly-drawn shape when the user
            // mouses down on it.
            google.maps.event.addListener(newShape, 'click', e => {
                if (!e) return;
                if (e.vertex !== undefined) {
                    if (newShape.type === google.maps.drawing.OverlayType.POLYGON) {
                        const path = newShape.getPaths().getAt(e.path);
                        path.removeAt(e.vertex);
                        if (path.length < 3) {
                            newShape.setMap(null);
                        }
                    }
                    else if (newShape.type === google.maps.drawing.OverlayType.POLYLINE) {
                        const path = newShape.getPath();
                        path.removeAt(e.vertex);
                        if (path.length < 2) {
                            newShape.setMap(null);
                        }
                    }
                }

                me.openShapeInfowindow(newShape, e.latLng);
            });
        }

        return this;
    }

    openShapeInfowindow(shape, position) {
        if (!this.shapes[shape.id]) return this;

        const me = this;
        let { infowindow } = this.shapes[shape.id];
        const { egId } = this.shapes[shape.id];
        const workWindow = this.workWindows[egId][shape.id];
        const source = workWindow.sourceId && this.egIdWiseList[workWindow.sourceId] ?
            this.egIdWiseList[workWindow.sourceId].name : "";
        const transporter = workWindow.transporterId && this.egIdWiseList[workWindow.transporterId] ?
            this.egIdWiseList[workWindow.transporterId].name : "";
        const start = moment.utc(workWindow.startTime).tz(this.reqParams.timezone);
        const end = moment.utc(workWindow.endTime).tz(this.reqParams.timezone);
        const startTime = start.format("hh:mm:ss A");
        const endTime = end.format("hh:mm:ss A");
        const duration = toDisplayTimeString(secondsToTime(end.diff(start, "seconds"), true));

        const contentString = `<div class='info-load'>
            <table/>
                <tr><td><span>Implement</span></td> <td>:</td> <td><label>${source}</label></td></tr>
                <tr><td><span>Transport</span></td> <td>:</td> <td><label>${transporter}</label></td></tr>
                <tr><td><span>Load Time</span></td> <td>:</td> <td><label>${startTime} to ${endTime}</label></td></tr>
                <tr><td><span>Duration</span></td> <td>:</td> <td><label>${duration}</label></td></tr>
            </table>
        </div>`;

        if (infowindow) {
            infowindow.setPosition(position);
        } else {
            infowindow = new this.google.maps.InfoWindow({
                content: contentString,
                disableAutoPan: true,
                position
            });

            infowindow.addListener("closeclick", function () { me.clearSelection(shape) });
            this.shapes[shape.id].infowindow = infowindow;
        }

        infowindow.open(this.map);
        this.setSelection(shape);
        return this;
    }

    setSelection(shape) {
        if (!shape) return this;
        if (shape.type === this.google.maps.drawing.OverlayType.MARKER) return this;
        this.shapes[shape.id].options = { strokeColor: shape.get('strokeColor'), strokeWeight: shape.get('strokeWeight') };
        shape.setOptions({ strokeColor: "#fff", strokeWeight: shape.get('strokeWeight') });
        return this;
    }

    clearSelection(shape) {
        if (!shape) return this;
        if (shape.type === this.google.maps.drawing.OverlayType.MARKER) return this;
        if (!this.shapes[shape.id].options) return this;
        shape.setOptions({ ...this.shapes[shape.id].options });
        return this;
    }

    closeShapeInfowindow(shape) {
        if (!this.shapes[shape.id]) return this;
        const shapeItem = this.shapes[shape.id];
        if (shapeItem.infowindow) shapeItem.infowindow.close();
        return this;
    }

    clear() {
        this.clearShapes();
        this.egIdWiseList = {};
        this.workWindows = {};
        this.shapes = {};
        this.reqParams = {
            egIds: [],
            farmId: null,
            timezone: null,
            startDate: null,
            endDate: null
        };
        this.emit(EVENT_CLEAR_STREAM);
        return this;
    }

    clearShapes() {
        for (const shapeId in this.shapes) {
            const item = this.shapes[shapeId];
            if (item.shape) item.shape.setMap(null);
            if (item.infowindow) item.infowindow.setMap(null);
            delete this.shapes[shapeId];
        }
        return this;
    }

    dispose() {
        this.clearShapes();
        return this;
    }

    _getShapeInstance() {
        if (!this.shapeObj) this.shapeObj = new Shape(this.google);
        return this.shapeObj;
    }
}